import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TeamView } from '@retro/shared';
import { api } from './api.js';

/** Remembers which team the shell was last showing, across navigations. */
const SELECTED_TEAM_KEY = 'retro:selectedTeamId';

type TeamsContextValue = {
  teams: TeamView[];
  activeTeam: TeamView | null;
  selectTeam: (teamId: string) => void;
  isLoading: boolean;
};

const TeamsContext = createContext<TeamsContextValue>({
  teams: [],
  activeTeam: null,
  selectTeam: () => {},
  isLoading: true,
});

export function TeamsProvider({
  children,
  enabled = true,
}: PropsWithChildren<{ enabled?: boolean }>) {
  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: api.teams.list, enabled });
  const teams = useMemo(() => teamsQuery.data?.teams ?? [], [teamsQuery.data]);

  // Kept in localStorage rather than state alone, which the dashboard would
  // forget the moment it unmounts.
  const [teamId, setTeamId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SELECTED_TEAM_KEY);
    } catch {
      return null;
    }
  });

  // A stored id for a team the user has since left must not strand the shell on
  // a blank selection, so fall back to the first team when it no longer exists.
  const activeTeam = teams.find((team) => team.id === teamId) ?? teams[0] ?? null;

  function selectTeam(id: string) {
    setTeamId(id);
    try {
      localStorage.setItem(SELECTED_TEAM_KEY, id);
    } catch {
      // Private-mode / storage-disabled: selection just falls back to in-memory.
    }
  }

  const value = useMemo<TeamsContextValue>(
    () => ({ teams, activeTeam, selectTeam, isLoading: teamsQuery.isLoading }),
    [teams, activeTeam, teamsQuery.isLoading],
  );

  return <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>;
}

export function useTeams() {
  return useContext(TeamsContext);
}
